import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import logoCBranca from '../assets/logo-c-branca.png';

// Paleta (apresentação institucional jun/2026)
const VERDE_ESCURO = '#0F3B1F';
const VERDE_ESCURO_2 = '#0A2A16';
const MOSTARDA = '#E2901A';
const MOSTARDA_CLARA = '#F5A623';
const OFFWHITE = '#F5F8F5';

// Máscara CPF/CNPJ pra exibição; deixa passar texto (login admin por nome).
function exibirComMascara(valor) {
  const v = String(valor || '');
  if (!v || /[^\d]/.test(v)) return v;
  if (v.length <= 11) {
    if (v.length <= 3) return v;
    if (v.length <= 6) return `${v.slice(0, 3)}.${v.slice(3)}`;
    if (v.length <= 9) return `${v.slice(0, 3)}.${v.slice(3, 6)}.${v.slice(6)}`;
    return `${v.slice(0, 3)}.${v.slice(3, 6)}.${v.slice(6, 9)}-${v.slice(9, 11)}`;
  }
  const d = v.slice(0, 14);
  if (d.length <= 12) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8)}`;
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12, 14)}`;
}

// O "C" como planta arquitetônica: traço de lápis + cotas que se redesenham + tijolos.
function BlueprintC() {
  return (
    <svg className="la-svg" viewBox="0 0 760 760" fill="none" aria-hidden="true">
      {/* eixos de construção — cruzam no centro do círculo do C, contidos */}
      <g stroke={OFFWHITE} strokeOpacity="0.18" strokeWidth="1.2" strokeDasharray="2 10">
        <line className="la-axis" x1="411" y1="95" x2="411" y2="690" />
        <line className="la-axis" x1="150" y1="398" x2="690" y2="398" />
      </g>

      {/* C branco — imagem em alta qualidade (logo institucional) */}
      <image className="la-cmass" href={logoCBranca}
        x="118" y="18" width="524" height="724" preserveAspectRatio="xMidYMid meet" />

      {/* cota de raio — parte do centro do círculo do C, redesenha em loop */}
      <g className="la-dim" stroke={MOSTARDA_CLARA} strokeWidth="1.6" fill="none">
        <line className="la-loop" x1="411" y1="398" x2="556" y2="398" />
        <path className="la-tip" d="M 546 392 L 556 398 L 546 404" />
        <circle cx="411" cy="398" r="3.5" fill={MOSTARDA_CLARA} stroke="none" />
      </g>
      <text x="458" y="388" fill={MOSTARDA_CLARA} fontSize="16" fontFamily="'Courier New', monospace" className="la-dimtext">R 215</text>

      {/* cota vertical externa — redesenha em loop */}
      <g className="la-dim2" stroke={OFFWHITE} strokeOpacity="0.55" strokeWidth="1.2" fill="none">
        <line className="la-loop2" x1="128" y1="205" x2="128" y2="560" />
        <path d="M 122 215 L 128 205 L 134 215" />
        <path d="M 122 550 L 128 560 L 134 550" />
      </g>
      <text x="92" y="392" fill={OFFWHITE} fillOpacity="0.6" fontSize="14" fontFamily="'Courier New', monospace"
        transform="rotate(-90 92 392)" className="la-dimtext">430 mm</text>

      {/* cota horizontal inferior — redesenha em loop (defasada) */}
      <g className="la-dim3" stroke={OFFWHITE} strokeOpacity="0.4" strokeWidth="1.2" fill="none">
        <line className="la-loop3" x1="218" y1="662" x2="552" y2="662" />
        <path d="M 228 656 L 218 662 L 228 668" />
        <path d="M 542 656 L 552 662 L 542 668" />
      </g>

      {/* marcações de canto */}
      <g stroke={OFFWHITE} strokeOpacity="0.4" strokeWidth="1.3" className="la-marks">
        <path d="M 580 178 h 20 M 590 168 v 20" />
        <path d="M 580 585 h 20 M 590 575 v 20" />
      </g>
    </svg>
  );
}

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [aba, setAba] = useState('login');
  const [modoAdmin, setModoAdmin] = useState(false);
  const [form, setForm] = useState({ username: '', senha: '' });
  const [esqueciCpf, setEsqueciCpf] = useState('');
  const [senhaVisivel, setSenhaVisivel] = useState(false);
  const [erro, setErro] = useState('');
  const [mensagem, setMensagem] = useState('');
  const [carregando, setCarregando] = useState(false);

  const sanitizarIdentificador = (bruto, setter) => {
    const apenasDigitosOuPontuacao = /^[\d.\-/]*$/.test(bruto);
    if (apenasDigitosOuPontuacao) setter(bruto.replace(/\D/g, '').slice(0, 14));
    else setter(bruto);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErro(''); setMensagem(''); setCarregando(true);
    try {
      const u = await login(form.username, form.senha);
      if (u?.senha_provisoria) navigate('/trocar-senha', { replace: true });
      else navigate('/dashboard');
    } catch (err) {
      let m;
      if (err.response?.data?.error) m = err.response.data.error;
      else if (err.message === 'Network Error') m = 'Erro de rede — não foi possível acessar o servidor.';
      else if (err.message) m = err.message;
      else m = 'Erro inesperado ao fazer login';
      setErro(m);
    } finally { setCarregando(false); }
  };

  const handleEsqueci = async (e) => {
    e.preventDefault();
    setErro(''); setMensagem(''); setCarregando(true);
    try {
      const r = await api.post('/auth/esqueci-senha', { identificador: esqueciCpf });
      setMensagem(r.data.mensagem || 'Se cadastrado, enviaremos uma nova senha por e-mail.');
      setEsqueciCpf('');
    } catch (err) {
      setErro(err.response?.data?.error || 'Erro ao solicitar nova senha');
    } finally { setCarregando(false); }
  };

  const trocarAba = (nova) => { setAba(nova); setErro(''); setMensagem(''); };
  const alternarModoAdmin = () => { setModoAdmin(v => !v); setForm({ username: '', senha: '' }); setErro(''); setMensagem(''); };

  return (
    <div className="la-wrap">
      <style>{CSS}</style>
      <div className="la-grid" />
      <div className="la-blueprint"><BlueprintC /></div>
      <div className="la-vinheta" />

      <div className="la-panel">
        <div className="la-left">
          {/* TEXTOS */}
          <div className="la-eyebrow">PORTAL DO ARQUITETO</div>
          <h1 className="la-title">
            Seu projeto vira<br /><span className="la-title-em">resultado.</span>
          </h1>
          <p className="la-lead">Acompanhe suas indicações e saque suas comissões por indicação.</p>

          {/* CAMPOS — sem card, flutuantes */}
          {erro && <div className="la-alert la-alert-err"><i className="bi bi-exclamation-circle" />{erro}</div>}
          {mensagem && <div className="la-alert la-alert-ok"><i className="bi bi-check-circle" />{mensagem}</div>}

          {aba === 'login' && (
            <form onSubmit={handleSubmit} className="la-form">
              <div className="la-field">
                <label>{modoAdmin ? 'Usuário' : 'CPF'}</label>
                {modoAdmin ? (
                  <input type="text" className="la-input" placeholder="seu usuário"
                    value={form.username} onChange={e => setForm({ ...form, username: e.target.value })}
                    autoFocus autoCapitalize="none" autoCorrect="off" spellCheck={false} autoComplete="username" required />
                ) : (
                  <input type="text" inputMode="numeric" className="la-input" placeholder="000.000.000-00"
                    value={exibirComMascara(form.username)}
                    onChange={e => sanitizarIdentificador(e.target.value, v => setForm({ ...form, username: v }))}
                    autoFocus autoCapitalize="none" autoCorrect="off" spellCheck={false} autoComplete="username" required />
                )}
              </div>

              <div className="la-field">
                <label>Senha</label>
                <div className="la-pass">
                  <input type={senhaVisivel ? 'text' : 'password'} className="la-input" placeholder="••••••••"
                    value={form.senha} onChange={e => setForm({ ...form, senha: e.target.value })}
                    autoCapitalize="none" autoCorrect="off" spellCheck={false} autoComplete="current-password" required />
                  <button type="button" className="la-eye" onClick={() => setSenhaVisivel(v => !v)} tabIndex={-1}
                    title={senhaVisivel ? 'Ocultar senha' : 'Mostrar senha'}>
                    <i className={`bi ${senhaVisivel ? 'bi-eye-slash' : 'bi-eye'}`} />
                  </button>
                </div>
              </div>

              <div className="la-row">
                {!modoAdmin
                  ? <button type="button" className="la-link" onClick={() => trocarAba('esqueci')}>Esqueci minha senha</button>
                  : <span className="la-hint"><i className="bi bi-info-circle" /> Gestores e diretores</span>}
              </div>

              <button type="submit" className="la-btn" disabled={carregando}>
                {carregando ? <><span className="spinner-border spinner-border-sm" />Entrando...</>
                  : <>Entrar <i className="bi bi-arrow-right" /></>}
              </button>
            </form>
          )}

          {aba === 'esqueci' && (
            <form onSubmit={handleEsqueci} className="la-form">
              <p className="la-muted">Informe seu CPF/CNPJ cadastrado. Se houver e-mail vinculado, enviaremos uma nova senha provisória.</p>
              <div className="la-field">
                <label>CPF / CNPJ</label>
                <input type="text" inputMode="numeric" className="la-input" placeholder="000.000.000-00"
                  value={exibirComMascara(esqueciCpf)}
                  onChange={e => sanitizarIdentificador(e.target.value, setEsqueciCpf)} autoFocus required />
              </div>
              <button type="submit" className="la-btn" disabled={carregando}>
                {carregando ? <><span className="spinner-border spinner-border-sm" />Enviando...</>
                  : <><i className="bi bi-envelope" /> Enviar nova senha</>}
              </button>
              <div className="la-row"><button type="button" className="la-link" onClick={() => trocarAba('login')}>← Voltar ao login</button></div>
            </form>
          )}

          {/* rodapé: alterna modo + assinatura */}
          {aba === 'login' && (
            <div className="la-foot">
              <button type="button" className="la-modo" onClick={alternarModoAdmin}>
                <i className={`bi ${modoAdmin ? 'bi-person-badge' : 'bi-shield-lock'}`} />
                {modoAdmin ? 'Sou arquiteto' : 'Acesso administrativo'}
              </button>
              <span className="la-assin">Centrão Materiais de Construção</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const CSS = `
.la-wrap{position:relative;min-height:100vh;overflow:hidden;background:radial-gradient(120% 120% at 78% 12%, ${VERDE_ESCURO} 0%, ${VERDE_ESCURO_2} 72%);display:flex;align-items:center;font-family:'Inter','Segoe UI',sans-serif}
.la-grid{position:absolute;inset:0;background-image:linear-gradient(rgba(245,248,245,.05) 1px,transparent 1px),linear-gradient(90deg,rgba(245,248,245,.05) 1px,transparent 1px);background-size:34px 34px;mask-image:radial-gradient(120% 120% at 75% 30%,#000 45%,transparent 92%);pointer-events:none}
.la-vinheta{position:absolute;inset:0;background:radial-gradient(70% 70% at 22% 50%, rgba(10,42,22,.6), transparent 72%);pointer-events:none}

/* C gigante livre à direita — protagonista */
.la-blueprint{position:absolute;top:50%;right:-3%;transform:translateY(-50%);width:min(60vw,860px);opacity:.97;pointer-events:none;transform-origin:55% 50%;animation:laBreathe 16s ease-in-out infinite}
.la-svg{width:100%;height:auto;display:block;filter:drop-shadow(0 22px 60px rgba(0,0,0,.4))}
.la-cmass{animation:laFade 1.2s ease both}
.la-cline{stroke-dasharray:1500;stroke-dashoffset:1500;animation:laDraw 2.6s ease .2s forwards}
.la-bricks rect{opacity:0;animation:laBrick .5s ease forwards}
.la-bricks rect:nth-child(1){animation-delay:1.0s}.la-bricks rect:nth-child(2){animation-delay:1.15s}.la-bricks rect:nth-child(3){animation-delay:1.3s}.la-bricks rect:nth-child(4){animation-delay:1.45s}
/* cotas que se traçam continuamente */
.la-loop{stroke-dasharray:200;animation:laTrace 8.5s ease-in-out 1.4s infinite}
.la-loop2{stroke-dasharray:440;animation:laTrace2 10.5s ease-in-out 2s infinite}
.la-loop3{stroke-dasharray:440;animation:laTrace2 10.5s ease-in-out 4.5s infinite}
.la-tip,.la-dim path,.la-dim2 path,.la-dim3 path{opacity:0;animation:laFade .8s ease 2.4s forwards}
.la-dimtext{opacity:0;animation:laFade .8s ease 2.6s forwards}
.la-marks{opacity:0;animation:laFade .8s ease 2.2s forwards}
.la-axis{opacity:0;animation:laFade 1s ease .1s forwards}

.la-panel{position:relative;z-index:3;width:100%;max-width:1180px;margin:0 auto;padding:40px clamp(22px,5vw,80px);display:flex;align-items:center}
.la-left{width:100%;max-width:420px;transform:translateY(-2vh);animation:laUp .8s ease both}
.la-eyebrow{display:inline-block;color:${MOSTARDA_CLARA};font-weight:700;letter-spacing:.26em;font-size:.72rem;border:1px solid rgba(245,166,35,.35);padding:6px 13px;border-radius:100px;margin-bottom:20px}
.la-title{color:${OFFWHITE};font-size:clamp(2.3rem,4.6vw,3.6rem);font-weight:800;line-height:1.02;letter-spacing:-.02em;margin:0 0 16px}
.la-title-em{color:${MOSTARDA_CLARA};position:relative}
.la-title-em:after{content:"";position:absolute;left:0;right:0;bottom:-.1em;height:.13em;background:${MOSTARDA};border-radius:4px;transform:scaleX(0);transform-origin:left;animation:laUnderline .8s ease 1s forwards}
.la-lead{color:rgba(245,248,245,.74);font-size:1.02rem;line-height:1.5;margin:0 0 30px;max-width:380px}

.la-form{display:block}
.la-field{margin-bottom:15px}
.la-field label{display:block;font-size:.8rem;font-weight:600;color:rgba(245,248,245,.82);margin-bottom:7px;letter-spacing:.01em}
.la-input{width:100%;box-sizing:border-box;padding:14px 16px;font-size:1rem;color:${OFFWHITE};background:rgba(255,255,255,.06);border:1.5px solid rgba(245,248,245,.16);border-radius:13px;outline:none;backdrop-filter:blur(10px);-webkit-backdrop-filter:blur(10px);transition:.18s}
.la-input::placeholder{color:rgba(245,248,245,.38)}
.la-input:focus{border-color:${MOSTARDA_CLARA};background:rgba(255,255,255,.1);box-shadow:0 0 0 4px rgba(245,166,35,.16)}
.la-input:-webkit-autofill,.la-input:-webkit-autofill:hover,.la-input:-webkit-autofill:focus,.la-input:-webkit-autofill:active{-webkit-text-fill-color:${OFFWHITE};caret-color:${OFFWHITE};transition:background-color 600000s 0s,color 600000s 0s}
.la-pass{position:relative}
.la-eye{position:absolute;right:8px;top:50%;transform:translateY(-50%);background:none;border:none;color:rgba(245,248,245,.55);font-size:1.05rem;cursor:pointer;padding:8px}
.la-row{display:flex;justify-content:flex-end;margin:4px 0 18px;min-height:18px}
.la-link{background:none;border:none;color:rgba(245,248,245,.72);font-size:.84rem;font-weight:500;cursor:pointer;padding:0}
.la-link:hover{color:${MOSTARDA_CLARA};text-decoration:underline}
.la-hint{color:rgba(245,248,245,.55);font-size:.8rem;display:inline-flex;align-items:center;gap:5px}
.la-btn{width:100%;display:flex;align-items:center;justify-content:center;gap:9px;padding:15px;font-size:1.02rem;font-weight:700;color:#1c1206;background:linear-gradient(135deg,${MOSTARDA_CLARA},${MOSTARDA});border:none;border-radius:13px;cursor:pointer;box-shadow:0 12px 28px rgba(226,144,26,.4);transition:.2s}
.la-btn:hover{transform:translateY(-2px);box-shadow:0 18px 38px rgba(226,144,26,.52)}
.la-btn:disabled{opacity:.7;cursor:default;transform:none}
.la-btn i{transition:transform .2s}
.la-btn:hover i{transform:translateX(3px)}
.la-alert{display:flex;align-items:center;gap:8px;font-size:.86rem;padding:11px 14px;border-radius:11px;margin-bottom:16px;backdrop-filter:blur(8px)}
.la-alert-err{background:rgba(214,69,57,.18);color:#ffd7d2;border:1px solid rgba(214,69,57,.35)}
.la-alert-ok{background:rgba(46,160,90,.18);color:#cdf4da;border:1px solid rgba(46,160,90,.35)}
.la-muted{font-size:.9rem;color:rgba(245,248,245,.72);margin-bottom:16px;line-height:1.5}
.la-foot{display:flex;flex-direction:column;gap:10px;margin-top:24px;padding-top:18px;border-top:1px solid rgba(245,248,245,.12)}
.la-modo{align-self:flex-start;display:inline-flex;align-items:center;gap:6px;background:rgba(245,248,245,.07);border:1px solid rgba(245,248,245,.16);color:rgba(245,248,245,.85);font-size:.78rem;font-weight:600;padding:7px 14px;border-radius:100px;cursor:pointer;transition:.18s}
.la-modo:hover{background:${MOSTARDA};border-color:${MOSTARDA};color:#fff}
.la-assin{color:rgba(245,248,245,.4);font-size:.72rem}

@keyframes laUp{from{opacity:0;transform:translateY(26px)}to{opacity:1;transform:translateY(-2vh)}}
@keyframes laFade{from{opacity:0}to{opacity:1}}
@keyframes laDraw{to{stroke-dashoffset:0}}
@keyframes laBrick{from{opacity:0;transform:translateY(-8px)}to{opacity:1;transform:none}}
@keyframes laUnderline{to{transform:scaleX(1)}}
@keyframes laBreathe{0%,100%{transform:translateY(-50%) scale(.97)}50%{transform:translateY(-50%) scale(1.06)}}
@keyframes laTrace{0%{stroke-dashoffset:200}28%{stroke-dashoffset:0}68%{stroke-dashoffset:0}100%{stroke-dashoffset:-200}}
@keyframes laTrace2{0%{stroke-dashoffset:440}30%{stroke-dashoffset:0}70%{stroke-dashoffset:0}100%{stroke-dashoffset:-440}}

@media (max-width:900px){
  .la-panel{justify-content:center;padding:48px 22px}
  .la-left{max-width:430px;transform:none;text-align:left}
  .la-blueprint{right:-32%;top:6%;width:96vw;opacity:.14;transform:none;animation:none}
}
@media (prefers-reduced-motion:reduce){
  .la-cline,.la-bricks rect,.la-dimtext,.la-marks,.la-axis,.la-title-em:after,.la-left,.la-blueprint,.la-loop,.la-loop2,.la-loop3{animation:none!important;stroke-dashoffset:0!important;opacity:1!important;transform:none!important}
  .la-blueprint{transform:translateY(-50%)!important}
}
`;
